// Copyright (c) Microsoft. All rights reserved.

// import { Image } from '@fluentui/react-components';
import { FC, useEffect, useCallback, useState } from 'react';
import { Conversations } from '../../../libs/models/conversations/ConversationsState_v2';
import { isToday, isYesterday, isLastWeek, isLastMonth, isLastYear } from '../../../components/utils/TextUtils';
import { ChatListSection } from './ChatListSection';
import InfiniteScroll from 'react-infinite-scroller';
// import LoadingIcon from '../../../assets/etc-icons/icon_loading_small.gif';

interface ConversationsView {
  toDayConversations?: Conversations;
  yesterDayConversations?: Conversations;
  lastWeekConversations?: Conversations;
  lastMonthConversations?: Conversations;
  lastYearConversations?: Conversations;
  olderConversations?: Conversations;
}

interface ChatListContainerProps {
  conversations: Conversations;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoading: boolean;
  scrollViewTargetRef: React.RefObject<HTMLDivElement>;
}

export const ChatListContainer: FC<ChatListContainerProps> = ({
  conversations,
  hasMore,
  onLoadMore,
  isLoading,
  scrollViewTargetRef,
}) => {
  const [conversationsView, setConversationsView] = useState<ConversationsView>({
    olderConversations: conversations,
  });
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const [initialHeight, setinitialHeight] = useState(scrollViewTargetRef.current?.scrollHeight ?? 0);

  const sortConversations = (conversations: Conversations): ConversationsView => {
    // sort conversations by last activity
    const sortedIds = Object.keys(conversations).sort((a, b) => {
      return conversations[a].createdTime.valueOf() - conversations[b].createdTime.valueOf();
    });
    // Add conversations to sortedConversations in the order of sortedIds.
    const toDayConversations: Conversations = {};
    const yesterDayConversations: Conversations = {};
    const lastWeekConversations: Conversations = {};
    const lastMonthConversations: Conversations = {};
    const lastYearConversations: Conversations = {};
    const olderConversations: Conversations = {};
    sortedIds.forEach((id) => {
      const chatDate = new Date(conversations[id].createdTime); // Assuming chat.date is a valid date string
      if (isToday(chatDate)) {
        toDayConversations[id] = conversations[id];
      } else if (isYesterday(chatDate)) {
        yesterDayConversations[id] = conversations[id];
      } else if (isLastWeek(chatDate)) {
        lastWeekConversations[id] = conversations[id];
      } else if (isLastMonth(chatDate)) {
        lastMonthConversations[id] = conversations[id];
      } else if (isLastYear(chatDate)) {
        lastYearConversations[id] = conversations[id];
      } else {
        olderConversations[id] = conversations[id];
      }
    });
    return {
      toDayConversations: toDayConversations,
      yesterDayConversations: yesterDayConversations,
      lastWeekConversations: lastWeekConversations,
      lastMonthConversations: lastMonthConversations,
      lastYearConversations: lastYearConversations,
      olderConversations: olderConversations,
    };
  };

  useEffect(() => {
    setConversationsView(sortConversations(conversations));
  }, [conversations]);

  useEffect(() => {
    if (!shouldAutoScroll) return;
    scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
  }, [shouldAutoScroll, scrollViewTargetRef]);

  const loadMore = () => {
    if (!hasMore || isLoading) return;
    onLoadMore();
  };

  const toggleOpen = useCallback(
    (state: boolean) => {
      setinitialHeight(scrollViewTargetRef.current?.scrollHeight ?? 0);

      if (initialHeight > (scrollViewTargetRef.current?.scrollHeight ?? 0)) {
        setShouldAutoScroll(true);
      }
      return !state;
    },
    [initialHeight, scrollViewTargetRef],
  );
  return (
    <nav className="chat-nav">
      <h2 className="blind">채팅 기록</h2>
      {Object.keys(conversations).length == 0 ? (
        <span className="nav-text">대화내역이 없습니다.</span>
      ) : (
        <InfiniteScroll
          loadMore={() => {
            loadMore();
          }}
          initialLoad={true}
          hasMore={hasMore}
          isReverse={false}
          useWindow={false}
          // loader={<Image src={LoadingIcon} key={0} style={{ margin: 'auto' }} />}
          // style={{ display: 'flex', flexDirection: 'column' }}
          getScrollParent={() => scrollViewTargetRef.current}
        >
          {conversationsView.toDayConversations && (
            <ChatListSection
              header="오늘"
              toggleOpen={toggleOpen}
              conversations={conversationsView.toDayConversations}
            />
          )}
          {conversationsView.yesterDayConversations && (
            <ChatListSection
              header="어제"
              toggleOpen={toggleOpen}
              conversations={conversationsView.yesterDayConversations}
            />
          )}
          {conversationsView.lastWeekConversations && (
            <ChatListSection
              header="최근 7일"
              toggleOpen={toggleOpen}
              conversations={conversationsView.lastWeekConversations}
            />
          )}
          {conversationsView.lastMonthConversations && (
            <ChatListSection
              header="최근 30일"
              toggleOpen={toggleOpen}
              conversations={conversationsView.lastMonthConversations}
            />
          )}
          {conversationsView.lastYearConversations && (
            <ChatListSection
              header="최근 1년"
              toggleOpen={toggleOpen}
              conversations={conversationsView.lastYearConversations}
            />
          )}
          {conversationsView.olderConversations && (
            <ChatListSection
              header="오래됨"
              toggleOpen={toggleOpen}
              conversations={conversationsView.olderConversations}
            />
          )}
        </InfiniteScroll>
      )}
    </nav>
  );
};
